import React, { Component } from "react";
import { Button, Icon, Modal, Dropdown, Input } from "semantic-ui-react";
import { SuccessMessage, ErrorMessage } from "../../../Notifications/messages";
import { invenioConfig } from "@config";

export default class WeedingThresholdButton extends Component {
  state = {
    open: false,
    selected: "",
    custom: "",
    currentThreshold: null,
    loadingThreshold: true,
    applying: false,
    clearing: false,
    showSuccessMessage: false,
    showErrorMessage: false,
    successContent: "",
    errorContent: "",
    messageId: Date.now(),
  };

  componentDidMount() {
    this._isMounted = true;
    this.fetchCurrentThreshold();
  }

  componentWillUnmount() {
    // Cancel any pending fetch requests
    if (this.applyAbortController) {
      this.applyAbortController.abort();
    }
    if (this.clearAbortController) {
      this.clearAbortController.abort();
    }
    if (this.fetchAbortController) {
      this.fetchAbortController.abort();
    }
    // Prevent setState calls after unmount
    this._isMounted = false;
  }

  fetchCurrentThreshold = async () => {
    try {
      const baseURL = invenioConfig.APP.INVENIO_UI_URL;
      this.fetchAbortController = new AbortController();

      const response = await fetch(`${baseURL}/custom/weeding/status`, {
        headers: { Accept: "application/json" },
        signal: this.fetchAbortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (this._isMounted) {
        this.setState({
          currentThreshold: data.current_threshold,
          loadingThreshold: false,
        });
      }
    } catch (error) {
      if (this._isMounted && error.name !== "AbortError") {
        this.setState({ loadingThreshold: false });
      }
    }
  };

  options = [
    { key: "10", value: 10, text: "10 years" },
    { key: "20", value: 20, text: "20 years" },
    { key: "50", value: 50, text: "50 years" },
    { key: "custom", value: "custom", text: "Custom" },
  ];

  openModal = () =>
    this.setState({
      open: true,
      showSuccessMessage: false,
      showErrorMessage: false,
    });

  closeModal = () =>
    this.setState({
      open: false,
      showSuccessMessage: false,
      showErrorMessage: false,
    });

  handleDropdownChange = (e, { value }) => {
    this.setState({ selected: value, custom: "" });
  };

  handleCustomChange = (e) => {
    this.setState({ custom: e.target.value });
  };

  removeNotification = () => {
    this.setState({
      showSuccessMessage: false,
      showErrorMessage: false,
    });
  };

  showSuccessNotification = (content) => {
    this.setState({
      showSuccessMessage: true,
      showErrorMessage: false,
      successContent: content,
      messageId: Date.now(),
    });
  };

  showErrorNotification = (content) => {
    this.setState({
      showSuccessMessage: false,
      showErrorMessage: true,
      errorContent: content,
      messageId: Date.now(),
    });
  };

  formatSuccessMessage = (data) => {
    const { processed, flagged, unflagged, skippedNoCopyright } = data;
    const { selected, custom } = this.state;
    const threshold = selected === "custom" ? custom : selected;

    let message =
      flagged === 0
        ? `Applied ${threshold}-year threshold.\n\nNo documents were flagged for weeding.\nThis means no documents have copyright years older than ${threshold} years.\n\n`
        : `Applied ${threshold}-year threshold successfully!\n\n`;

    message += `Results:\n• Processed: ${processed} documents\n`;
    message += `• Flagged for weeding: ${flagged} documents\n`;
    if (unflagged > 0) message += `• Unflagged: ${unflagged} documents\n`;
    if (skippedNoCopyright > 0)
      message += `• Skipped (no copyright): ${skippedNoCopyright} documents\n`;

    message +=
      flagged === 0
        ? `\nTry a lower threshold value to flag older documents.`
        : `\nSearch index updated automatically.`;

    return message;
  };

  formatClearSuccessMessage = (data) => {
    let message = `Cleared weeding threshold successfully!\n\n`;
    return message;
  };

  handleApplyThreshold = () => {
    const baseURL = invenioConfig.APP.INVENIO_UI_URL;
    const { selected, custom } = this.state;
    const threshold = selected === "custom" ? custom : selected;

    if (!threshold || isNaN(threshold) || parseInt(threshold) < 1) {
      this.showErrorNotification(
        !threshold || isNaN(threshold)
          ? "Please enter a valid threshold value."
          : "Threshold must be at least 1 year."
      );
      return;
    }

    this.setState({ applying: true });
    this.applyAbortController = new AbortController();

    fetch(`${baseURL}/custom/weeding/apply`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ threshold: parseInt(threshold) }),
      signal: this.applyAbortController.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.error || `HTTP error! status: ${response.status}`
          );
        }
        return response.json();
      })
      .then((data) => {
        if (!this._isMounted) return;

        const successMessage = this.formatSuccessMessage(data);

        this.setState({
          currentThreshold: parseInt(threshold),
          applying: false,
          open: false,
        });

        this.showSuccessNotification(successMessage);

        setTimeout(() => {
          window.location.reload();
        }, 2000);
      })
      .catch((error) => {
        if (!this._isMounted || error.name === "AbortError") return;

        this.setState({ applying: false });
        this.showErrorNotification(
          error.message || "Failed to apply weeding threshold."
        );
      });
  };

  handleClearThreshold = () => {
    const baseURL = invenioConfig.APP.INVENIO_UI_URL;

    this.setState({ clearing: true });
    this.clearAbortController = new AbortController();

    fetch(`${baseURL}/custom/weeding/apply`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ threshold: 0 }),
      signal: this.clearAbortController.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.error || `HTTP error! status: ${response.status}`
          );
        }
        return response.json();
      })
      .then((data) => {
        if (!this._isMounted) return;

        const successMessage = this.formatClearSuccessMessage(data);

        this.setState({
          currentThreshold: null,
          clearing: false,
          open: false,
          selected: "",
          custom: "",
        });

        this.showSuccessNotification(successMessage);

        setTimeout(() => {
          window.location.reload();
        }, 2000);
      })
      .catch((error) => {
        if (!this._isMounted || error.name === "AbortError") return;

        this.setState({ clearing: false });
        this.showErrorNotification(
          error.message || "Failed to clear weeding threshold."
        );
      });
  };

  getButtonText = () => {
    const { currentThreshold, loadingThreshold } = this.state;

    if (loadingThreshold) return "Set Weeding Threshold (Loading...)";
    if (currentThreshold)
      return `Set Weeding Threshold (${currentThreshold} years)`;
    return "Set Weeding Threshold";
  };

  render() {
    const {
      open,
      selected,
      custom,
      applying,
      clearing,
      showSuccessMessage,
      showErrorMessage,
      successContent,
      errorContent,
      currentThreshold,
      messageId,
    } = this.state;

    const isOperationInProgress = applying || clearing;

    return (
      <>
        {showSuccessMessage && (
          <div
            style={{
              position: "fixed",
              top: "20px",
              right: "20px",
              zIndex: 1000,
              maxWidth: "400px",
            }}
          >
            <SuccessMessage
              id={messageId}
              header="Success!"
              content={successContent}
              removeNotification={this.removeNotification}
            />
          </div>
        )}

        {showErrorMessage && (
          <div
            style={{
              position: "fixed",
              top: "20px",
              right: "20px",
              zIndex: 1000,
              maxWidth: "400px",
            }}
          >
            <ErrorMessage
              id={messageId}
              header="Error"
              content={errorContent}
              removeNotification={this.removeNotification}
            />
          </div>
        )}

        <Button
          icon
          positive
          size="small"
          labelPosition="left"
          onClick={this.openModal}
          className="weeding-threshold-button"
        >
          <Icon name="flag" />
          {this.getButtonText()}
        </Button>

        {currentThreshold && (
          <div
            style={{
              color: "#666",
              right: "20px",
              textAlign: "right",
            }}
          >
            Click again to include new documents
          </div>
        )}

        <Modal open={open} onClose={this.closeModal} size="small">
          <Modal.Header>
            {currentThreshold
              ? "Manage Weeding Threshold"
              : "Set Weeding Threshold"}
          </Modal.Header>
          <Modal.Content>
            <p>
              Documents with copyright years older than the threshold will be
              flagged for weeding.
            </p>

            {currentThreshold && (
              <div
                style={{
                  padding: "0.8rem",
                  backgroundColor: "#fff3cd",
                  borderRadius: "4px",
                  fontSize: "0.9em",
                  marginBottom: "1em",
                  border: "1px solid #ffeaa7",
                }}
              >
                <strong>Current threshold:</strong> {currentThreshold} years
                <br />
                <small>
                  Documents with copyright year before{" "}
                  <strong>{2025 - currentThreshold}</strong> are currently
                  flagged.
                </small>
              </div>
            )}

            <div
              style={{
                padding: "0.8rem",
                backgroundColor: "#e8f4f8",
                borderRadius: "4px",
                fontSize: "0.9em",
                marginBottom: "1em",
              }}
            >
              <strong>Tip:</strong> If you've added new documents recently,
              apply the same threshold again to include them in weeding.
            </div>

            <Dropdown
              placeholder="Select Threshold"
              fluid
              selection
              options={this.options}
              onChange={this.handleDropdownChange}
              value={selected}
              disabled={isOperationInProgress}
            />

            {selected === "custom" && (
              <Input
                type="number"
                min="1"
                fluid
                placeholder="Enter custom threshold in years"
                value={custom}
                onChange={this.handleCustomChange}
                style={{ marginTop: "1em" }}
                disabled={isOperationInProgress}
              />
            )}

            {(selected || custom) && (
              <div
                style={{
                  marginTop: "1em",
                  padding: "1em",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "4px",
                }}
              >
                Documents with copyright year before{" "}
                <strong>
                  {2025 - (selected === "custom" ? custom || 0 : selected || 0)}
                </strong>{" "}
                will be flagged for weeding.
              </div>
            )}
          </Modal.Content>

          <Modal.Actions>
            <Button onClick={this.closeModal} disabled={isOperationInProgress}>
              Cancel
            </Button>

            {currentThreshold && (
              <Button
                color="orange"
                onClick={this.handleClearThreshold}
                loading={clearing}
                disabled={isOperationInProgress}
              >
                <Icon name="remove" />
                Clear Threshold
              </Button>
            )}

            <Button
              primary
              onClick={this.handleApplyThreshold}
              loading={applying}
              disabled={isOperationInProgress || !selected}
            >
              <Icon name="flag" />
              Apply Threshold
            </Button>
          </Modal.Actions>
        </Modal>
      </>
    );
  }
}
